import * as React from 'react';

import { spacing } from '@gitbook/react-uikit';
import type { Document } from '@gitbook/slate';
import type { EditorContainer } from '@gitbook/slate-react';

import { getBlockEstimatedHeight } from './heights';

/**
 * Return true if a node is estimated to be visible in the viewport.
 */
export function useIsNodeEstimatedInViewport(editor: EditorContainer, nodeKey: string): boolean {
    return React.useMemo(
        () => isNodeEstimatedInViewport(editor.value.document, nodeKey),
        [
            // As we only estimate for the initial rendering, we memoize based on `editor` which is a stable reference
            // It avoid running the estimation on each edit.
            editor,
            nodeKey,
        ]
    );
}

function isNodeEstimatedInViewport(document: Document, nodeKey: string): boolean {
    const nodePath = document.getPath(nodeKey);
    if (!nodePath) {
        return false;
    }

    // < instead of <= because paths are 0-indexed
    return nodePath[0] < getNodesViewportRange(document);
}

const cache = new WeakMap<Document, number>();

function getNodesViewportRange(document: Document): number {
    if (cache.has(document)) {
        return cache.get(document)!;
    }

    const viewportHeight = 800;
    let height = 0;

    for (let index = 0; index < document.nodes.size; index++) {
        const node = document.nodes.get(index);
        if (!node) {
            break;
        }

        height += getBlockEstimatedHeight(node, document, {
            blockSpacing: {
                paddingTop: spacing.medium,
                paddingBottom: spacing.medium,
            },
        });

        if (height > viewportHeight) {
            cache.set(document, index);
            return index;
        }
    }

    cache.set(document, document.nodes.size);
    return document.nodes.size;
}
